import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { type ColumnDef, Grid, SecondaryData } from '@strata/react-grid';
import { MessagingBlock } from '@strata/react-messaging-block';
import { Icon } from '@strata/react-status-icons';
import { useState } from 'react';

import { DeleteFillActivityDialog } from './dialogs/DeleteFillActivityDialog';

import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { AllFillActivitiesMap, VisibleFillActivityOptionsSet } from 'services/fillActivityHelpers';
import { type FillActivityEvent } from 'store/api/userSupportApi';

const initialSortProperties = [{ id: 'eventTimestamp', desc: true }];

export const SYSTEM_SOURCE = 'SYSTEM';
export const FMH_USER = 'FMH User';

const formatDocumentedBy = (eventSource: FillActivityEvent['eventSource']) => {
    if (eventSource === SYSTEM_SOURCE) {
        return <span className="sds_fill-activity__system-source">System</span>;
    }
    return eventSource;
};

const getColumns: (
    setDeleteDialogOpen: (event: FillActivityEvent) => void,
    writeOnboarding: boolean,
) => ColumnDef<FillActivityEvent>[] = (setDeleteDialogOpen, writeOnboarding) => [
    {
        accessorKey: 'eventStatus',
        header: 'Fill Activity',
        size: 200,
    },
    {
        accessorKey: 'eventSource',
        header: 'Documented By',
        size: 150,
        cell: ({ getValue }) => <SecondaryData data={formatDocumentedBy(getValue())} />,
    },
    {
        accessorKey: 'eventTimestamp',
        header: 'Documented On',
        size: 50,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'eventId',
        header: () => <Icon icon={IconNames.TRASH} />,
        enableSorting: false,
        size: 5,
        cell: ({ row }) => {
            const { eventSource } = row.original;
            return eventSource !== SYSTEM_SOURCE && eventSource !== FMH_USER && writeOnboarding ? (
                <Icon
                    icon={IconNames.TRASH}
                    intent={Icon.Intent.DEFAULT}
                    tooltipContent="Delete Fill Activity"
                    onClick={() => setDeleteDialogOpen(row.original)}
                />
            ) : null;
        },
    },
];

interface FillActivity {
    events?: FillActivityEvent[];
    isLoading?: boolean;
}

export const FillActivity = ({ events = undefined, isLoading = true }: FillActivity) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<FillActivityEvent | undefined>();
    const writeOnboarding = usePermissionCheck(Permissions.WRITE_ONBOARDING);

    const visibleEvents = events
        ?.filter(
            (event) => !!event.eventStatus && VisibleFillActivityOptionsSet.has(event.eventStatus),
        )
        .map((event) => ({
            ...event,
            eventStatus: event.eventStatus ? AllFillActivitiesMap[event.eventStatus] : undefined,
        }));

    return (
        <>
            <DeleteFillActivityDialog
                isOpen={!!deleteDialogOpen}
                eventId={deleteDialogOpen?.eventId}
                referralId={deleteDialogOpen?.referralId}
                eventStatus={deleteDialogOpen?.eventStatus}
                onClose={() => setDeleteDialogOpen(undefined)}
            />
            <Grid
                data={visibleEvents}
                isLoading={isLoading}
                sortProperties={initialSortProperties}
                noResults={
                    <div className="padding__around--large">
                        <MessagingBlock
                            size={MessagingBlock.Sizes.SMALL}
                            icon={IconNames.DOCUMENT}
                            primaryText="No fill activities documented"
                        />
                    </div>
                }
                columns={getColumns(setDeleteDialogOpen, writeOnboarding)}
            />
        </>
    );
};
